import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext} from 'react'
import {Link} from 'react-router-dom'
import {useSelector} from 'react-redux'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {DelayRender} from 'views/Shared/components/DelayRender'
import WorkspaceContext from '../../WorkspaceContext'
import {formatNumber} from '../../../../helpers/number'
import getTitle from '../../../../helpers/getTitle'
import {getType} from '../../../../apis/model/base'
import {bindQueryParams} from '../../../AppLocals/functions/routerHelper'
import {Avatar, Skeleton} from 'antd'
import {FaChalkboard, FaUserGraduate, FaUserTie} from 'react-icons/fa6'
import {ClassroomSvg} from '../../../../components/Svgs/ClassroomSvg'
import {
    course_getCourseClassesByOwner_Api,
    course_getCourseEnrollmentsByOwner_Api,
    course_getCourses_Api,
    modelAudit_getModelAudits_Api,
    timetable_getsByClassesOwner_Api,
} from 'apis'
import {BsCalendar2Event, BsClock, BsExclamationCircle, BsPeopleFill,} from 'react-icons/bs'
import {MdOutlineTimeline} from 'react-icons/md'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import CustomLazyPaginationWrapper from 'modules/asyncCache/components/CustomLazyPaginationWrapper'
import {getLogin} from 'redux/auth/selectors'
import {enrollment_status_options} from 'views/AppLocals/Course'
import getLabelOfGender from '../../../../helpers/getLabelOfGender'
import {IdentifierTypes} from '../../../AppLocals/appFieldTypes'
import Toggle from 'components/Toggle'
import {buildCourseRelLink} from '../functions/courseWorkspaceLinks'
import CourseNotificationsPanel from './CourseNotificationsPanel'
import CourseSchedulePanel from './CourseSchedulePanel'

dayjs.extend(relativeTime)

const colorMap = {
    indigo: {bg: '#eaf1f7', text: '#1c4a75', icon: '#2c5f8f', badge: '#dcfce7', badgeText: '#16a34a'},
    orange: {bg: '#fff7ed', text: '#c2410c', icon: '#ea580c', badge: '#ffedd5', badgeText: '#9a3412'},
    green: {bg: '#ecfdf5', text: '#047857', icon: '#059669', badge: '#d1fae5', badgeText: '#065f46'},
    blue: {bg: '#eff6ff', text: '#1d4ed8', icon: '#3b82f6', badge: '#dbeafe', badgeText: '#1e40af'},
}

const getBarColor = (pct, hasQuota) => {
    if (!hasQuota) return '#94a3b8'   // xám → không giới hạn
    if (pct >= 100) return '#ef4444' // đỏ → đầy chỉ tiêu
    return '#22c55e'                  // xanh → còn chỗ
}

const actionConfig = {
    create: {color: '#16a34a', bg: '#f0fdf4', label: 'create'},
    update: {color: '#2563eb', bg: '#eff6ff', label: 'update'},
    delete: {color: '#dc2626', bg: '#fef2f2', label: 'delete'},
    restore: {color: '#ea580c', bg: '#fff7ed', label: 'restore'},
}

// Field kỹ thuật (snake_case, khớp $auditOnly bên BE) -> key i18n đã có sẵn trong hệ thống
// (khác tên field do các key i18n này được đặt theo ngữ cảnh form, không phải theo tên cột DB).
// Field nào không có trong map sẽ fallback qua _.startCase ở getAuditFieldLabel bên dưới.
const auditFieldLabelKeys = {
    contact_email: 'email',
    contact_phone: 'phone',
    contact_address: 'address',
    date_of_birth: 'date of birth',
    place_of_birth: 'place of birth',
    curriculum_id: 'curriculum',
    subject_ids: 'subject',
    diocese_name: 'diocese',
    patron_saint_name: 'patron saint',
    religious_order_name: 'religious order',
    prefix_title: 'prefix title',
    identifier_code: 'identifier',
    identifier_type: 'identifier',
    time_open: 'start date',
    time_close: 'end date',
    last_enrollment_time: 'last enrollment time',
    student_quota: 'student quota',
    from_date: 'from date',
    to_date: 'to date',
    score_type: 'score type',
    score_options: 'score options',
    grade_date: 'grade date',
    attendance_date: 'attendance date',
    number_of_sessions: 'number of sessions',
    academic_year: 'academic year',
    academic_field: 'academic field',
}

const getAuditFieldLabel = (t, field) => {
    const key = auditFieldLabelKeys[field] || field
    const translated = t(key)
    return translated && translated !== key ? translated : _.startCase(field)
}

// Field lưu dạng mã số (0/1/2...) mà FE đã có sẵn bảng nhãn tra cứu - khác field boolean thường
// (được BE trả đúng kiểu boolean nên formatAuditFieldValue tự nhận diện, không cần khai báo ở đây).
const auditFieldValueFormatters = {
    gender: (t, value) => {
        // 0 = chưa cập nhật (quy ước dùng chung, xem renderStudent.js/renderTeacher.js check
        // `gender !== 0` để ẩn badge giới tính) - GenderTypes không có mục value:0 nên phải bắt
        // riêng, nếu không formatter sẽ fallback hiện số thô "0".
        if (value === null || value === undefined || value === '' || Number(value) === 0) return '—'
        const label = getLabelOfGender(Number(value))
        return label ? t(label) : String(value)
    },
    active: (t, value) => {
        if (value === null || value === undefined || value === '') return '—'
        return t(Number(value) === 1 ? 'yes' : 'no')
    },
    identifier_type: (t, value) => {
        if (!value) return '—'
        const label = _.find(IdentifierTypes, {value})?.label
        return label ? t(label) : String(value)
    },
}

const formatAuditFieldValue = (t, field, value) => {
    if (auditFieldValueFormatters[field]) return auditFieldValueFormatters[field](t, value)
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'boolean') return t(value ? 'yes' : 'no')
    const str = String(value)
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
        const d = dayjs(str)
        if (d.isValid()) return d.format('DD/MM/YYYY')
    }
    // Cắt bớt text tự do quá dài (description/about...) - tránh 1 dòng đổi field tràn ra cả đoạn
    // văn dài (đã từng xảy ra: mô tả khóa học vô tình dán nhầm nội dung tài liệu Ant Design dài
    // cả trăm chữ), làm rối cả bảng Nhật ký hoạt động.
    return str.length > 50 ? `${str.slice(0, 50)}…` : str
}

const formatScheduleDate = (t, start_time) => {
    if (!start_time) return ''
    const date = dayjs(start_time)
    const suffix = `, ${date.format('HH:mm')}`
    if (date.isSame(dayjs(), 'day')) return `${t('today')}${suffix}`
    return `${t(`day_short_${date.day()}`)}, ${date.format('DD/MM')}${suffix}`
}

// Placeholder cho DelayRender.LoadingComponent (khối CHƯA MOUNT, khác skeleton NỘI BỘ từng khối -
// đã có sẵn trong mỗi component riêng cho lúc "đã mount, đang chờ API"). Chỉ cần đúng chiều cao gần
// đúng để không giật layout khi khối thật thế chỗ - không cần khớp pixel-by-pixel vì tồn tại rất
// ngắn (time ms) trước khi khối thật tự mount và tự lo phần skeleton chi tiết của nó.
const SectionSkeleton = ({height = 160}) => (
    <div style={{height}} className="w-full rounded-3xl background-100 animate-pulse"/>
)

// "Thẻ chỉ số" (KPI/stat card) - title nhỏ + icon màu góc phải trên. Số LỚN = số ĐANG CHẠY thật của
// tổ chức (khóa đang chiêu sinh/vận hành, lớp/học viên/giảng viên đang hoạt động) - đây là con số
// quản trị viên cần quan tâm hàng ngày, không phải tổng dữ liệu lịch sử. Dòng phụ bên dưới mới là
// tổng cộng toàn bộ dữ liệu trong org (kể cả đã đóng/ngừng) - CHỈ hiện khi có giá trị ý nghĩa
// (subText undefined thì ẩn hẳn dòng đó). Trước đây 2 vai trò này bị đảo ngược (số lớn = tổng, dòng
// phụ = đang hoạt động) khiến quản trị viên phải nhìn vào con số phụ nhỏ mới thấy tình hình thật.
// Export - CourseClassOperations.js (băng thống kê đầu trang "Điều hành Khóa & Lớp học") dùng LẠI
// nguyên component này thay vì tự vẽ 1 kiểu thẻ chỉ số song song khác.
export const StatCard = ({title, value, subText, color, icon, href}) => {
    const p = colorMap[color] || colorMap.indigo
    return (
        <Link
            to={href}
            className="p-3 md:p-4 background rounded-2xl border border-color-50 hover:border-slate-300 shadow-sm hover:shadow-md flex flex-col gap-1.5 no-underline transition-all">
            <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-xs text-slate-400 uppercase tracking-wider leading-snug truncate">
                    {title}
                </span>
                <span style={{color: p.icon}} className="shrink-0">{icon}</span>
            </div>
            <span style={{color: p.text}} className="text-2xl md:text-3xl font-bold tracking-tight leading-none">
                {value}
            </span>
            {!!subText && (
                <span style={{color: p.badgeText}} className="text-xs font-medium leading-none">
                    {subText}
                </span>
            )}
        </Link>
    )
}

const QuickActionPill = ({icon, title, href, color = '#1c4a75'}) => (
    <Link
        to={href}
        className="flex items-center gap-2 background hover:bg-slate-100 border border-slate-200 hover:border-slate-300 text-sm no-underline transition-all shrink-0"
        style={{
            color: color,
            padding: '0.5rem 1rem',
            borderRadius: '0.65rem',
            fontWeight: 600,
        }}>
        <span style={{color}} className="flex">{icon}</span>
        {title}
    </Link>
)

const EnrollmentProgressBar = ({title, count, quota, courseState}) => {
    const t = useTranslate()
    const hasQuota = quota !== null && quota > 0
    const pct = hasQuota ? Math.min(100, Math.round((count / quota) * 100)) : 0
    const barColor = getBarColor(pct, hasQuota)

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-slate-700 truncate">{title}</span>
                <span
                    className="text-xs font-bold shrink-0 tabular-nums"
                    style={{color: barColor}}>
          {hasQuota
              ? `${formatNumber(count)} / ${formatNumber(quota)} ${t('student quota')} (${pct}%)`
              : `${formatNumber(count)} ${t('students')} / ${t('unlimited')}`}
        </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                        width: hasQuota ? `${pct}%` : '100%',
                        background: barColor,
                        opacity: 1,
                    }}
                />
            </div>
        </div>
    )
}

const AuditTableRow = ({item}) => {
    const t = useTranslate()
    if (!item) return null
    const knownCfg = actionConfig[item.action]
    const cfg = knownCfg || {color: '#9ca3af', bg: '#f9fafb'}
    const label = knownCfg ? t(cfg.label) : item.action
    const user = item.user
    const timeStr = item.last_activity_at ? dayjs(item.last_activity_at).format('HH:mm') : ''
    const dateStr = item.last_activity_at
        ? dayjs(item.last_activity_at).format(dayjs(item.last_activity_at).year() === dayjs().year() ? 'DD/MM' : 'DD/MM/YYYY')
        : ''
    const initials = user?.name ? user.name.charAt(0).toUpperCase() : '?'

    // changes: {field: {old, new}} hoặc {field: {changed: true}} (field dạng auditSummaryOnly, vd 'about')
    const changeEntries = Object.entries(item.changes || {})
    // content._type: 'teacher'/'student'/'course'/'course_class'... - khớp trực tiếp key i18n có
    // sẵn (t('teacher') = "Giảng viên"...) - cần hiện rõ loại đối tượng, không chỉ tên, để tránh
    // đọc nhầm khi content_name trùng tên người thực hiện (vd admin tự tạo hồ sơ giảng viên
    // mang tên chính mình -> nếu chỉ hiện tên sẽ đọc như "X tạo ra X").
    const contentType = item.content?._type

    return (
        // Mobile: 4 cột 70px/1fr/90px/2fr của bản desktop bóp "Nội dung" xuống còn ~1 gang tay, chữ
        // vỡ ra nhiều dòng khiến mỗi hàng cao ngất (đã gặp thực tế trên 375px). Base (mobile) chuyển
        // hẳn sang flex-col: Thời gian/Người dùng/Hành động gộp 1 hàng ngang gọn phía trên, Nội dung
        // xuống hàng dưới CHIẾM TRỌN bề rộng. course-audit-row (+ course-audit-row-group trên div
        // gộp) khôi phục NGUYÊN layout 4-cột cũ từ 640px - 2 class custom vì Tailwind v1 (bản đang
        // dùng) không có grid-cols-[...] (arbitrary value) lẫn display:contents, xem
        // tailwind_custom.css.
        <div
            className="flex flex-col gap-1.5 py-3 border-b border-slate-50 last:border-b-0 course-audit-row sm:items-start sm:gap-3">
            <div className="flex items-center gap-2 flex-wrap course-audit-row-group">
                <div className="flex items-center gap-1.5 sm:flex-col sm:items-center sm:gap-0 sm:leading-tight">
                    <span className="font-mono font-medium text-sm sm:text-base text-slate-500">{timeStr}</span>
                    <span className="text-2xs text-slate-400 tabular-nums">{dateStr}</span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                    {user?.avatar
                        ? <Avatar size={22} src={user.avatar}/>
                        : (
                            <Avatar
                                size={22}
                                style={{background: '#e0e7ff', color: '#4f46e5', fontWeight: 600, fontSize: 10}}>
                                {initials}
                            </Avatar>
                        )}
                    <span className="text-sm sm:text-base font-semibold text-slate-700 truncate">{user?.name ?? '—'}</span>
                </div>
                <div>
                    <span
                        className="px-1 py-0.5 rounded text-sm font-semibold whitespace-nowrap"
                        style={{background: cfg.bg, color: cfg.color}}>
                        {label}
                    </span>
                </div>
            </div>
            <div className="flex flex-col gap-1 min-w-0">
                <span className="text-base font-semibold text-slate-700 leading-snug break-words">
                    {!!contentType && (
                        <span className="text-slate-400 font-normal">{t(contentType)}: </span>
                    )}
                    {item.content_name || item.description || '—'}
                </span>
                {item.action === 'update' && changeEntries.length > 0 && (
                    <Toggle>
                        {(isToggle, toggle) => (
                            <div className="flex flex-col gap-0.5">
                                <span
                                    onClick={() => toggle(!isToggle)}
                                    className="text-xs text-blue-500 hover:text-primary-600 italic cursor-pointer w-fit">
                                    {isToggle ? `${t('see less')}...` : `${t('view detail')} (${changeEntries.length})`}
                                </span>
                                {isToggle && changeEntries.map(([field, change]) => (
                                    <span key={field} className="text-xs text-slate-400 leading-snug break-words">
                                        {change?.changed
                                            ? `${getAuditFieldLabel(t, field)} (${t('changed')})`
                                            : `${getAuditFieldLabel(t, field)}: ${formatAuditFieldValue(t, field, change?.old)} → ${formatAuditFieldValue(t, field, change?.new)}`}
                                    </span>
                                ))}
                            </div>
                        )}
                    </Toggle>
                )}
            </div>
        </div>
    )
}

const UpcomingSchedule = ({owner_id, owner_type}) => {
    const t = useTranslate()

    const {response, loading} = useAsync({
        apiInfo: timetable_getsByClassesOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
        values: {status: 1},
    })
    // timetables.from_date là ngày BẮT ĐẦU phân công (đợt lịch định kỳ), không phải ngày diễn ra
    // buổi học — lọc "sắp tới" theo field này bỏ sót mọi lớp đã bắt đầu dạy từ trước nhưng vẫn còn
    // buổi trong tương lai. Phải duyệt timetable_details[].start_time (buổi cụ thể đã được sinh ra
    // từ pattern lặp lại), giống cách CourseClassTimetable.js (trang lịch) đang làm.
    const items = _.chain(_.get(response, 'data.data', []))
        .flatMap(item => (item.timetable_details ?? []).map(detail => ({
            ...detail,
            course_class: item.course_class,
            is_holiday: item.is_holiday,
            // location_address thuộc bản ghi timetable CHA (item), không phải từng detail - phải
            // mang theo thủ công qua flatMap để hiện được "buổi học này ở đâu" (dùng bên dưới).
            location_address: item.location_address,
        })))
        .filter(lesson => dayjs(lesson.end_time).isAfter(dayjs()))                   // đang diễn ra hoặc sắp tới
        .groupBy(lesson => _.get(lesson, 'course_class.id'))                         // group theo lớp
        .map(group => _.minBy(group, lesson => dayjs(lesson.start_time).valueOf()))  // gần nhất mỗi lớp
        .filter(Boolean)
        .sortBy(lesson => dayjs(lesson.start_time).valueOf())
        .take(5)
        .value()
    const isToday = (date) => dayjs(date).isSame(dayjs(), 'day')

    return (
        <div className="border border-slate-200 rounded-2xl shadow-sm flex flex-col">
            <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100 bg-slate-50 rounded-t-2xl">
                <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
                    <BsCalendar2Event size={15} style={{color: '#2c5f8f'}}/>
                    {t('timetables')}
                </h3>
                <Link
                    to={buildCourseRelLink({rel: 'course-class-timetable'})}
                    style={{color: '#2c5f8f'}}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#1c4a75' }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#2c5f8f' }}
                    className="text-xs font-semibold">
                    {t('view all')}
                </Link>
            </div>
            <div className="flex-1 p-4 background rounded-b-2xl">
                {loading && (
                    <div className="flex flex-col gap-3">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} active paragraph={{rows: 1}} title={false}/>
                        ))}
                    </div>
                )}
                {!loading && items.length === 0 && (
                    <div className="py-8 text-center text-sm text-slate-300 italic">
                        {t('no upcoming schedule')}
                    </div>
                )}
                {!loading && items.length > 0 && (
                    <div className="flex flex-col gap-4">
                        {items.map((item) => {
                            const dateStr = formatScheduleDate(t, item.start_time)
                            const courseTitle = _.get(item, 'course_class.course.title')
                            const classTitle = _.get(item, 'course_class.title')
                            // Ưu tiên tên LỚP HỌC + địa điểm/trực tuyến làm thông tin CHÍNH (đúng
                            // buổi học này diễn ra ở lớp nào, ở đâu) - tên KHÓA chỉ còn là tham chiếu
                            // PHỤ bên dưới. Trước đây ngược lại (tên khóa to, tên lớp nhỏ) khiến
                            // không thấy ngay buổi học thuộc lớp nào/họp ở đâu.
                            const primaryTitle = item.is_holiday
                                ? `${item.notes ? ` - ${item.notes}` : ''}`
                                : classTitle || courseTitle || ''
                            const showCourseRef = !item.is_holiday && !!courseTitle && courseTitle !== primaryTitle
                            const todayItem = isToday(item.start_time)
                            return (
                                <div key={item.id} className="flex items-start gap-3">
                                    <div
                                        className="mt-2 w-2 h-2 rounded-full"
                                        style={{background: todayItem ? 'var(--color-secondary-600)' : '#cbd5e1'}}
                                    />
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                        <span className={`font-semibold text-sm tracking-wide ${todayItem ? 'text-secondary-600' : 'text-gray-600'}`}>
                                            {dateStr}
                                        </span>
                                        <span className="font-medium text-color-000 leading-snug">
                                            {primaryTitle}
                                            {!item.is_holiday && (
                                                <span className="font-normal text-color-300">
                                                    {' · '}{item.location_address || t('online')}
                                                </span>
                                            )}
                                        </span>
                                        {showCourseRef && (
                                            <span className="font-medium text-xs text-color-300 italic leading-snug tracking-wide">
                                                {courseTitle}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

const AlertRow = ({href, icon, iconBg, iconColor, title, description, actionLabel, actionColor, actionBg}) => (
    <Link
        to={href}
        className="py-3 first:pt-0 last:pb-0 flex items-center gap-3 no-underline">
        <div
            style={{background: iconBg, color: iconColor}}
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0">
            {icon}
        </div>
        <div className="flex flex-col min-w-0 flex-1 gap-0.5">
            <span className="text-sm font-bold text-slate-800 truncate">{title}</span>
            {!!description && (
                <span className="text-xs text-slate-500 truncate">{description}</span>
            )}
        </div>
        <span
            style={{background: actionBg, color: actionColor}}
            className="px-3.5 py-2 rounded-xl text-xs font-bold shrink-0 whitespace-nowrap">
            {actionLabel}
        </span>
    </Link>
)

const AlertsPanel = ({owner_id, owner_type}) => {
    const t = useTranslate()

    const {response: enrollResp, isLoading: isEnrollLoading} = useAsync({
        apiInfo: course_getCourseEnrollmentsByOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
        values: {status: enrollment_status_options.pending.value},
    })

    const {response: classResp, isLoading: isClassLoading} = useAsync({
        apiInfo: course_getCourseClassesByOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
    })

    // Trước khi có response, pendingCount/allClasses mặc định rỗng -> dễ lầm với "thật sự không có
    // gì cần xử lý" (return null) trong khi thật ra CHỈ đang loading - tách riêng skeleton, không
    // để lọt qua nhánh "không có cảnh báo" rồi popping-in đột ngột khi data về.
    if (isEnrollLoading || isClassLoading) {
        return (
            <div className="p-4 md:p-6 flex flex-col gap-3 border border-color-50 rounded-2xl animate-pulse">
                {[1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                        <div style={{width: 36, height: 36}} className="shrink-0 background-200 rounded-xl"/>
                        <div className="flex-1 flex flex-col gap-1.5">
                            <div style={{height: 12, width: '50%'}} className="background-200 rounded"/>
                            <div style={{height: 10, width: '30%'}} className="background-200 rounded"/>
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    const pendingCount = _.get(enrollResp, 'data.meta.pagination.total', 0)
    const allClasses = _.get(classResp, 'data.data', [])
    const noScheduleClasses = allClasses.filter((c) => {
        if (c.has_timetable) return false
        if (c.to_date && dayjs().isAfter(dayjs(c.to_date), 'day')) return false // lớp đã kết thúc, không cần thiết lập lịch nữa
        const timeClose = _.get(c, 'course.time_close')
        if (!c.to_date && timeClose && dayjs().isAfter(dayjs(timeClose), 'day')) return false // lớp không có to_date riêng, fallback theo khóa đã kết thúc
        return true
    })
    const visibleNoScheduleClasses = noScheduleClasses.slice(0, 3)

    if (!pendingCount && noScheduleClasses.length === 0) return null

    return (
        <div className="flex flex-col border border-amber-300 rounded-2xl shadow-md">
            <div className="px-4 md:px-6 py-3 md:py-4 flex items-center gap-2 border-b border-amber-300 bg-amber-50 rounded-t-2xl">
                <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-500 flex items-center justify-center shrink-0">
                    <BsExclamationCircle size={15}/>
                </div>
                <h3 className="font-bold text-slate-800 text-sm truncate">{t('needs attention')}</h3>
            </div>
            {/* Cùng 1 tông màu hổ phách (amber) cho MỌI hàng - khớp header ở trên, không tự ý pha
                đỏ/cam riêng cho từng loại cảnh báo nữa (đã gây rối mắt, không đồng nhất trong 1
                khối). Phân biệt loại cảnh báo bằng ICON, không bằng màu. */}
            <div className="px-4 md:px-6 py-1 flex flex-col divide-y divide-slate-100 background rounded-b-2xl">
                {pendingCount > 0 && (
                    <AlertRow
                        href={buildCourseRelLink({rel: 'register-for-course'})}
                        icon={<BsPeopleFill size={15}/>}
                        iconBg="#fef3c7"
                        iconColor="#d97706"
                        title={`${pendingCount} ${t('pending enrollments')}`}
                        actionLabel={t('review now')}
                        actionColor="#b45309"
                        actionBg="#fffbeb"
                    />
                )}
                {visibleNoScheduleClasses.map((c) => (
                    <AlertRow
                        key={c.id}
                        href={buildCourseRelLink({rel: 'course-class', 'class-id': c.id, 'class-tab': 'schedule'})}
                        icon={<BsCalendar2Event size={15}/>}
                        iconBg="#fef3c7"
                        iconColor="#d97706"
                        title={c.title}
                        description={t('no schedule')}
                        actionLabel={t('setup now')}
                        actionColor="#b45309"
                        actionBg="#fffbeb"
                    />
                ))}
            </div>
        </div>
    )
}

const SystemAuditLog = ({owner_id, owner_type}) => {
    const t = useTranslate()

    if (!owner_id || !owner_type) return null

    return (
        <CustomLazyPaginationWrapper
            id={`model-audits-${owner_type}-${owner_id}`}
            apiInfo={modelAudit_getModelAudits_Api}
            query={bindQueryParams([{type: owner_type}, {id: owner_id}])}
            values={{group_type: 'course'}}
            alwaysVisible={true}
            // auto=false: nút "Xem thêm" bấm tay mới load trang kế - KHÔNG tự load khi cuộn tới
            // cuối (infinite scroll mặc định của CustomLazyPagination khi auto=true).
            auto={false}
            renderItem={(item) => <AuditTableRow item={item}/>}
            renderLoading={() => (
                <div className="flex flex-col gap-4 px-6 py-4">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} active avatar={{size: 22}} paragraph={{rows: 1}}/>
                    ))}
                </div>
            )}
            renderEmpty={() => (
                <div className="flex items-center justify-center px-6 py-8">
                    <p className="text-sm text-slate-300 italic text-center">{t('no activity')}</p>
                </div>
            )}
            renderWrap={(child) => (
                <div className="p-4 flex flex-col background rounded-b-2xl">
                    {/* Header cột chỉ có nghĩa khi hàng bên dưới thật sự xếp theo đúng 4 cột (sm+) -
                        mobile giờ gộp Thời gian/Người dùng/Hành động vào 1 hàng ngang (AuditTableRow),
                        không còn thẳng cột với header nữa nên ẩn hẳn, đỡ rối mắt trên màn hẹp. */}
                    <div
                        className="hidden sm:block course-audit-row gap-3 pb-3 border-b border-slate-100 mb-1">
                        <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">
                            {t('time')}
                        </span>
                        <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">
                            {t('user')}
                        </span>
                        <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">
                            {t('action')}
                        </span>
                        <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">
                            {t('content')}
                        </span>
                    </div>
                    {child}
                </div>
            )}
        />
    )
}

const CourseDashboard = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)
    const login = useSelector(getLogin) || {}

    const {response: coursesResponse, isLoading: isCoursesLoading} = useAsync({
        apiInfo: course_getCourses_Api,
        query: bindQueryParams([{type: getType(organization)}, {id: organization?.id}]),
    })

    const allCourses = _.get(coursesResponse, 'data.data', [])
    // activeCourses: PHỤ mảng cho "Tiến độ tuyển sinh" bên dưới - cố ý giới hạn thêm "mới mở trong 3
    // tuần" (không cần xem tiến độ tuyển sinh của khóa mở đã lâu). KHÔNG dùng biến này cho thẻ chỉ số
    // "khóa học" (activeCourseCount bên dưới) - 2 mục đích khác nhau, gộp chung sẽ ra số sai (thiếu
    // khóa đang chiêu sinh/vận hành nhưng mở đã hơn 3 tuần).
    const activeCourses = allCourses.filter((c) => {
        if (!['enrollment_open', 'in_process'].includes(c.course_state)) return false
        if (!c.time_open) return true
        const timeOpen = dayjs(c.time_open)
        return dayjs().diff(timeOpen, 'week') <= 3
    })
    // Số "đang chạy" cho thẻ chỉ số Khóa học - TOÀN BỘ khóa đang chiêu sinh (enrollment_open) hoặc
    // đang vận hành (in_process), không giới hạn thời gian mở như activeCourses ở trên.
    const activeCourseCount = allCourses.filter((c) => ['enrollment_open', 'in_process'].includes(c.course_state)).length
    const stats = organization?.workspace_settings?.courses?.statistics ?? {}

    const orgType = getType(organization)
    const orgId = organization?.id

    return (
        <div className="flex flex-col gap-6">

            {/* Header - flex-col mobile / flex-row md+ (tiêu đề + 2 nút Học viên/Khóa đào tạo trước
                đây LUÔN nằm chung 1 hàng kể cả mobile hẹp, ép khối nút bị bóp hẹp -> 2 pill wrap
                chồng lên nhau trông vỡ layout, bạn báo lỗi này) - xếp chồng dọc trên mobile, quay lại
                1 hàng ngang từ md trở lên, đúng pattern flex-col md:flex-row đã dùng ở các header
                khác trong Course module (Courses.js...). */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('overview')}
                    </h2>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <div className="mt-1 text-sm text-color-300 tracking-wider">
                        {t('start setting up your new training content')}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <QuickActionPill
                        icon={<BsPeopleFill size={16}/>}
                        title={t('student')}
                        href={buildCourseRelLink({rel: 'student'})}
                        color="#059669"
                    />
                    <QuickActionPill
                        icon={<FaChalkboard size={16}/>}
                        title={t('course')}
                        href={buildCourseRelLink({rel: 'course'})}
                        color="#1c4a75"
                    />
                </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
                <StatCard
                    title={t('courses')}
                    value={formatNumber(activeCourseCount)}
                    subText={stats.course_count > 0 ? `${formatNumber(stats.course_count)} ${t('total')}` : undefined}
                    color="indigo"
                    icon={<FaChalkboard size={18}/>}
                    href={buildCourseRelLink({rel: 'course'})}
                />
                <StatCard
                    title={t('classes')}
                    value={formatNumber(stats.active_course_class_count)}
                    subText={stats.course_class_count > 0 ? `${formatNumber(stats.course_class_count)} ${t('total')}` : undefined}
                    color="orange"
                    icon={<ClassroomSvg size={18}/>}
                    href={buildCourseRelLink({rel: 'course'})}
                />
                <StatCard
                    title={t('students')}
                    value={formatNumber(stats.active_student_count)}
                    subText={stats.student_count > 0 ? `${formatNumber(stats.student_count)} ${t('total')}` : undefined}
                    color="green"
                    icon={<FaUserGraduate size={18}/>}
                    href={buildCourseRelLink({rel: 'student'})}
                />
                <StatCard
                    title={t('teacher')}
                    value={formatNumber(stats.active_teacher_count)}
                    subText={stats.teacher_count > 0 ? `${formatNumber(stats.teacher_count)} ${t('total')}` : undefined}
                    color="blue"
                    icon={<FaUserTie size={18}/>}
                    href={buildCourseRelLink({rel: 'teacher'})}
                />
            </div>

            {/* Quản lý Thông báo Hệ thống & Lớp học - dữ liệu THẬT từ hệ thống Announcement chung
                (organization + course_class), xem ghi chú đầu CourseNotificationsPanel.js. */}
            <CourseNotificationsPanel
                owner_id={orgId}
                owner_type={orgType}
                orgTitle={getTitle(organization)}
                orgStudentCount={stats.student_count}
            />

            {/* Thống Kê Lịch Học & Phòng Học - dữ liệu THẬT từ timetable_getsByClassesOwner_Api
                (CÙNG API UpcomingSchedule bên dưới đang dùng), xem ghi chú đầu CourseSchedulePanel.js.
                Cố tình KHÔNG bỏ UpcomingSchedule (card nhỏ trong lưới "Middle" bên dưới) - 2 nơi phục
                vụ mục đích khác nhau (khối này xem đầy đủ/đổi chế độ, card kia liếc nhanh 5 buổi kế
                tiếp cạnh tiến độ tuyển sinh) - có trùng lặp nội dung, nói bạn biết nếu muốn bỏ bớt.

                standaloneCard - khối này đứng top-level cùng cấp CourseNotificationsPanel ngay phía
                trên, cần khung ngoài (nền/viền/bo góc/shadow) khớp y hệt khối đó ở MỌI kích thước màn
                hình - mặc định của CourseSchedulePanel lại tuning riêng cho ngữ cảnh lồng trong
                CourseClassTimetableTab.js (mất khung ở màn rộng), khiến khối lệch hẳn tông với các
                khối khác cùng trang (bạn báo lỗi này, kèm ảnh) - xem comment standaloneCard trong
                CourseSchedulePanel.js.

                DelayRender (time tăng dần theo vị trí xuống dưới màn hình) - trì hoãn THỜI ĐIỂM
                MOUNT (không phải ẩn/hiện CSS suông) của các khối càng xa phía trên, kéo theo useAsync
                bên trong cũng chỉ bắt đầu gọi ở đúng lúc đó - dàn request ra thay vì bắn hết cùng lúc
                lúc trang vừa tải, giảm tải dồn dập lên server. */}
            <DelayRender lazy={true} time={150} LoadingComponent={() => <SectionSkeleton height={280}/>}>
                <CourseSchedulePanel owner_id={orgId} owner_type={orgType} standaloneCard/>
            </DelayRender>

            {/* Middle: progress + upcoming schedule */}
            <DelayRender lazy={true} time={300} LoadingComponent={() => <SectionSkeleton height={320}/>}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                    {/* Enrollment progress */}
                    <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col">
                        <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100 bg-slate-50 rounded-t-2xl">
                            <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
                                <BsClock size={15} style={{color: '#2c5f8f'}}/>
                                {t('enrollment status')}
                            </h3>
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                                {activeCourses.length} {t('course')}
                            </span>
                        </div>
                        <div className="p-4 flex-1 background rounded-b-2xl">
                            {isCoursesLoading ? (
                                <div className="flex flex-col gap-4">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="flex flex-col gap-2 animate-pulse">
                                            <div style={{height: 14, width: '40%'}} className="background-200 rounded"/>
                                            <div style={{height: 8, width: '100%'}} className="background-200 rounded-full"/>
                                        </div>
                                    ))}
                                </div>
                            ) : activeCourses.length === 0 ? (
                                <div className="py-10 text-center text-sm text-slate-300 italic">
                                    {t('no data')}
                                </div>
                            ) : (
                                <div className="flex flex-col divide-y divide-gray-200">
                                    {activeCourses.map((course, idx) => (
                                        <div key={course.id} className="py-4 first:pt-0 last:pb-0">
                                            <EnrollmentProgressBar
                                                index={idx}
                                                title={course.title}
                                                count={course.student_count ?? 0}
                                                quota={course.student_quota ?? 0}
                                                courseState={course.course_state}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Upcoming schedule */}
                    <UpcomingSchedule
                        owner_id={orgId}
                        owner_type={orgType}
                    />
                </div>
            </DelayRender>

            {/* Alerts */}
            <DelayRender lazy={true} time={450} LoadingComponent={() => <SectionSkeleton height={100}/>}>
                <AlertsPanel
                    owner_id={orgId}
                    owner_type={orgType}
                />
            </DelayRender>

            {/* System audit log */}
            <DelayRender lazy={true} time={600} LoadingComponent={() => <SectionSkeleton height={260}/>}>
                <div className="flex flex-col background border border-color-50 rounded-2xl shadow-sm">
                    <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 rounded-t-2xl flex items-center justify-between">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
                            <MdOutlineTimeline size={15} style={{color: '#2c5f8f'}}/>
                            {t('activity log')}
                        </h3>
                    </div>
                    <SystemAuditLog
                        owner_id={orgId}
                        owner_type={orgType}
                    />
                </div>
            </DelayRender>

            <div className="h-4"/>
        </div>
    )
}

export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(CourseDashboard)
